import { mockAccount } from "core/mocks";
import { NO_ENCRYPTION_DENIED } from "core/model/patients/encryption/utils";
import { Account, Event } from "core/types";
import { ReactNode, createContext, useContext } from "react";

type AuctionDefaultContextType = {
  auctionRequestId: number | string | null | undefined;
  careproviderId: number | null | undefined;
};

const AuctionDefaultContext: AuctionDefaultContextType = {
  auctionRequestId: null,
  careproviderId: null,
};

export const AuctionRequestContext = createContext<AuctionDefaultContextType>(
  AuctionDefaultContext,
);

export function useAuctionRequestContext(): AuctionDefaultContextType {
  return useContext(AuctionRequestContext);
}

export const PrintContext = createContext<boolean>(false);

export function usePrint() {
  return useContext(PrintContext);
}

type EncryptionContextType = {
  accessDenied?: boolean;
  account?: Account;
  encryptionError?: string;
  events?: Array<Event> | null;
  hasEncryption: boolean;
  withEncryptionAccess: boolean;
  isSealdOnly: boolean;
  userSupportsEncryption?: boolean;
  encryptionAvailable: boolean;
  decryptedSessionKey: CryptoKey | null;
};

export const EncryptionContext = createContext<EncryptionContextType>({
  accessDenied: false,
  account: undefined,
  decryptedSessionKey: null,
  encryptionAvailable: false,
  encryptionError: undefined,
  events: null,
  hasEncryption: false,
  isSealdOnly: false,
  userSupportsEncryption: undefined,
  withEncryptionAccess: false,
});

export function useEncryption(): EncryptionContextType {
  return useContext(EncryptionContext);
}

export const EncryptionContextProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: Omit<EncryptionContextType, "encryptionAvailable" | "reason">;
}) => {
  const encryptionAvailable = value.hasEncryption && !value.accessDenied;
  return (
    <EncryptionContext.Provider
      value={{
        ...value,
        encryptionAvailable,
        encryptionError:
          value.encryptionError ||
          (encryptionAvailable ? undefined : NO_ENCRYPTION_DENIED),
      }}
    >
      {children}
    </EncryptionContext.Provider>
  );
};

export function MockedEncryptionProvider({
  account,
  children,
  events,
  hasEncryption,
}: {
  account?: Account;
  children: ReactNode;
  events: Event[];
  hasEncryption?: boolean;
}) {
  return (
    <EncryptionContextProvider
      value={{
        accessDenied: false,
        account: account || mockAccount({ id: 1 }),
        decryptedSessionKey: null,
        events,
        hasEncryption: !!hasEncryption,
        isSealdOnly: false,
        userSupportsEncryption: true,
        withEncryptionAccess: !!hasEncryption,
      }}
    >
      {children}
    </EncryptionContextProvider>
  );
}
